import { useState, useCallback, useEffect } from 'react';

/**
 * Hook for selecting browser audio output devices via setSinkId().
 * Uses feature detection — works in any browser that supports the API
 * (Chrome, Edge, Firefox, Safari 17.4+).
 */
export default function useAudioOutputDevices() {
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('default');

  const isSupported = typeof HTMLMediaElement.prototype.setSinkId === 'function';

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices || !isSupported) return;

    try {
      let list = await navigator.mediaDevices.enumerateDevices();
      let outputs = list.filter((d) => d.kind === 'audiooutput');

      // Some browsers (Safari) need a getUserMedia grant before
      // enumerateDevices returns labeled output devices.
      if (outputs.length === 0 || outputs.every((d) => !d.label)) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
          list = await navigator.mediaDevices.enumerateDevices();
          outputs = list.filter((d) => d.kind === 'audiooutput');
        } catch {
          // Permission denied — continue with whatever we have
        }
      }

      setDevices(
        outputs.map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Audio Output ${i + 1}`,
          isDefault: d.deviceId === 'default',
        }))
      );
    } catch (err) {
      console.error('[AudioOutputDevices] Enumerate failed:', err);
    }
  }, [isSupported]);

  const selectDevice = useCallback(async (deviceId) => {
    if (!isSupported) return false;

    try {
      const nodes =
        typeof document !== 'undefined'
          ? Array.from(document.querySelectorAll('audio'))
          : [];
      if (nodes.length === 0) return false;

      await Promise.all(nodes.map((el) => el.setSinkId(deviceId)));
      setActiveDeviceId(deviceId);
      return true;
    } catch (err) {
      console.error('[AudioOutputDevices] setSinkId failed:', err);
      return false;
    }
  }, [isSupported]);

  useEffect(() => {
    if (!navigator.mediaDevices || !isSupported) return;
    void refreshDevices();
  }, [refreshDevices, isSupported]);

  useEffect(() => {
    if (!navigator.mediaDevices || !isSupported) return;
    const handler = () => refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
  }, [refreshDevices, isSupported]);

  return { devices, activeDeviceId, selectDevice, refreshDevices, isSupported };
}
